import { useMutation, useQueryClient } from 'react-query';
import { checkoutHostedPage } from 'modules/api';
import RelativeLoading from '../../../../common/AtomicDesign/RelativeLoading';

declare global {
  interface Window {
    Chargebee: {
      getInstance: () => {
        openCheckout: (options: {
          hostedPage: () => Promise<{ url: string }>;
          success: (hostedPageId: string) => void;
          close: () => void;
          error: (error: unknown) => void;
        }) => void;
      };
    };
  }
}

type Props = {
  id: string;
  price: number;
  credits: number;
  type: string;
  chargebeeItemPriceId?: string;
  perAmount?: number;
  totalAmount?: number;
  isActive?: boolean;
  theme: string;
  isLoading: boolean;
  savingsPercent?: number;
  isFeatured?: boolean;
};

const CreditOfferCard: React.FC<Props> = ({
  id,
  price,
  credits,
  type,
  chargebeeItemPriceId,
  theme,
  perAmount,
  totalAmount,
  isActive,
  isLoading,
  savingsPercent,
  isFeatured = false,
}) => {
  const queryClient = useQueryClient();

  const { mutateAsync: createCheckout, isLoading: loadingCheckout } = useMutation(
    checkoutHostedPage,
  );

  const handleBuyClick = () => {
    if (!chargebeeItemPriceId) return;

    const chargebeeInstance = window.Chargebee.getInstance();

    chargebeeInstance.openCheckout({
      hostedPage: async () => {
        const data = await createCheckout({
          chargeItems: [
            {
              itemPriceId: chargebeeItemPriceId,
              quantity: 1,
            },
          ],
        });
        if (data?.hostedPage?.url) {
          return { url: data.hostedPage.url };
        }
        throw new Error(data?.error || 'Failed to get checkout URL');
      },
      success: () => {
        queryClient.invalidateQueries('getUserCredits');
        queryClient.invalidateQueries('fetchUserCredits');
      },
      close: () => {
        // Modal closed by user
      },
      error: (error) => {
        console.error('Chargebee checkout error:', error);
      },
    });
  };

  const renderBadge = () => {
    switch (type) {
      case 'most-popular':
        return (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
            <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">
              ✨ Most Popular
            </div>
          </div>
        );
      case 'best-value':
        return (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">
              🏆 Best Value
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const getCardStyles = () => {
    const baseStyles = 'relative font-figtree flex flex-col items-center justify-center bg-white rounded-xl transition-all duration-300 ease-out';
    const paddingStyles = isFeatured ? 'p-8' : 'p-5';
    const shadowStyles = 'shadow-md hover:shadow-xl';
    const hoverStyles = 'hover:-translate-y-1';
    const borderStyles = 'border border-neutral-200 hover:border-neutral-300';

    return `${baseStyles} ${paddingStyles} ${shadowStyles} ${hoverStyles} ${borderStyles}`;
  };

  return (
    <div className="relative">
      {renderBadge()}
      <div id={id} className={getCardStyles()}>
        <RelativeLoading isLoading={isLoading || loadingCheckout} />
        <div className={`mt-2 font-bold text-neutral-800 ${isFeatured ? 'text-4xl' : 'text-2xl'}`}>
          {credits.toLocaleString()}
        </div>
        <div className={`font-medium text-neutral-500 ${isFeatured ? 'text-base' : 'text-sm'}`}>
          Credits
        </div>
        <div className={`mt-2 text-neutral-400 ${isFeatured ? 'text-base' : 'text-sm'}`}>
          ${perAmount} each
        </div>
        {savingsPercent && savingsPercent > 0 ? (
          <div className={`mt-1 inline-flex items-center rounded-full font-semibold bg-success-50 text-success-700 ${isFeatured ? 'px-2 py-0.5 text-sm' : 'px-2 py-0.5 text-xs'}`}>
            Save {savingsPercent}%
          </div>
        ) : (
          <div className="mt-1 h-5" />
        )}
        <button
          type="button"
          onClick={handleBuyClick}
          disabled={loadingCheckout}
          className={`bg-primary-500 text-neutral-800 font-semibold rounded-full
            shadow-md hover:shadow-lg hover:shadow-primary-500/40
            transition-all duration-200 ease-out
            hover:bg-primary-400
            active:scale-95
            disabled:opacity-50 disabled:cursor-not-allowed
            ${isFeatured ? 'mt-4 py-3 px-8 text-base whitespace-nowrap' : 'mt-3 py-2 px-3 text-xs leading-tight'}`}
        >
          {loadingCheckout ? (
            '...'
          ) : (
            <span>
              Buy for <span className="font-bold">${totalAmount?.toLocaleString()}</span>
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default CreditOfferCard;
